<template>
  <div
    class="sidebar bg-white shadow-md py-5 md:py-8 fixed inset-y-0 top-0 right-0 transform transition duration-200 ease-in-out z-20 h-screen w-2/3 overflow-y-scroll"
    :class="[showMenu ? '-translate-x-0' : 'translate-x-full']"
    ref="menu"
  >
    <div class="flex flex-col items-stretch h-full">
      <div class="flex flex-col">
        <div @click="toggleMenu" class="flex justify-end items-center px-3">
          <XIcon class="text-brand-dark-gray w-5 h-5" />
        </div>
      </div>

      <div class="flex flex-col py-3" v-if="currentForm">
        <div class="flex flex-row py-1 px-5 justify-between">
          <div
            class="flex items-center py-1"
            :class="[
              'cursor-pointer',
              sidePanelState === 'add' ? 'border-b-2 border-brand-green' : ''
            ]"
            @click="setState('add')"
          >
            <p
              :class="[
                'cursor-pointer',
                sidePanelState === 'add' ? 'text-brand-green' : 'text-brand-dark-gray'
              ]"
            >
              Add fields
            </p>
          </div>

          <div
            class="flex items-center py-1"
            :class="[
              'cursor-pointer',
              sidePanelState === 'content' ? 'border-b-2 border-brand-green' : ''
            ]"
            @click="setState('content')"
          >
            <p
              :class="[
                'cursor-pointer',
                sidePanelState === 'content'
                  ? 'text-brand-green font-semibold'
                  : 'text-brand-dark-gray'
              ]"
            >
              Form content
            </p>
          </div>
        </div>

        <template v-if="sidePanelState === 'add'">
          <div class="flex flex-col py-3 px-2">
            <h5 class="text-sm py-1">Select the section to add to:</h5>
            <div class="flex py-2">
              <DropDown
                :initial="section"
                placeholder="Select section"
                :options="currentForm.sections"
                @select="updateSection"
              />
            </div>

            <p class="text-sm text-brand-dark-gray py-1">Fields:</p>

            <div
              v-for="field in fieldDefaults.filter((f) => f.display_in_builder)"
              :key="`side-panel-field-${field.field_id}`"
            >
              <div
                v-if="
                  field.field_id !== 'agent' ||
                  (getCurrentUser.landlord.agent_field_enabled &&
                    getCurrentUser.landlord.in_company)
                "
                class="py-2"
              >
                <AddFieldOption :field="field" @click="addField(field)" />
              </div>
            </div>
          </div>
        </template>

        <template v-else>
          <div class="flex flex-col py-3 px-2">
            <div
              class="flex flex-row rounded-lg border-2 my-3 w-full draggable-item relative"
              :class="[sectionIndex === index ? 'border-brand-green' : 'border-brand-light-gray']"
              v-for="(section, index) in currentForm.sections"
              :key="index"
              @click.stop="setCurrentSection(index)"
            >
              <div class="flex flex-col w-full py-1 pl-3 pr-2">
                <h5 class="py-1 font-semibold">{{ section.label }}</h5>
                <p
                  class="py-1 pl-6 text-sm text-brand-dark-gray truncate"
                  v-for="field in section.fields"
                  :key="field.name"
                >
                  {{ field.label }}
                </p>
              </div>

              <template v-if="sectionIndex === index">
                <div
                  class="w-6 h-6 bg-brand-green absolute -bottom-3 rounded-xl flex items-center justify-center"
                  :style="{
                    left: `${
                      screenWidth <= 320
                        ? screenWidth / 3.0 / 2.0 - 5
                        : screenWidth / 3.0 / 2.0 + 15
                    }px`
                  }"
                  @click.stop="moveSection('down', index)"
                >
                  <ChevronDownIcon class="w-5 h-5 text-white" />
                </div>
                <div
                  class="w-6 h-6 bg-brand-green absolute -bottom-3 rounded-xl flex items-center justify-center"
                  :style="{
                    right: `${
                      screenWidth <= 320 ? screenWidth / 3.0 / 2.0 : screenWidth / 3.0 / 2.0 + 15
                    }px`
                  }"
                  @click.stop="moveSection('up', index)"
                >
                  <ChevronUpIcon class="w-5 h-5 text-white" />
                </div>
              </template>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
<script>
import { XIcon, ChevronDownIcon, ChevronUpIcon } from "@heroicons/vue/outline"
import AddFieldOption from "./AddFieldOption.vue"

import _ from "lodash"

import fieldDefaults from "@/constants/fieldDefaults"

import { mapGetters } from "vuex"

export default {
  emits: ["close"],
  components: {
    XIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    AddFieldOption
  },
  props: {
    showMenu: {
      type: Boolean
    },
    currentSectionIndex: {
      type: Number,
      default: 0
    },
    currentForm: {
      type: Object
    }
  },
  computed: {
    ...mapGetters(["getCurrentUser"])
  },

  data() {
    return {
      sidePanelState: "add",
      fieldDefaults: [],
      section: {},
      sectionIndex: 0,
      screenWidth: window.innerWidth
    }
  },
  created() {
    this.setCurrentSection(this.currentSectionIndex)
    window.addEventListener("resize", this.handleResize)
  },
  mounted() {
    this.fieldDefaults = [...fieldDefaults]
  },
  unmounted() {
    window.removeEventListener("resize", this.handleResize)
  },

  watch: {
    currentSectionIndex: {
      handler(val) {
        this.setCurrentSection(val)
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    handleResize() {
      this.screenWidth = window.innerWidth
    },
    addField(field) {
      this.$emit("close", { sectionIndex: this.sectionIndex, field })
    },
    toggleMenu() {
      this.$emit("close")
    },
    setState(state) {
      this.sidePanelState = state
    },
    setCurrentSection(index) {
      this.sectionIndex = index
      this.section = this.currentForm.sections[index]
    },
    updateSection(section) {
      this.sectionIndex = _.findIndex(this.currentForm.sections, (s) => s === section)
      this.section = section
    },
    moveSection(direction, index) {
      if (direction === "up" && index === 0) {
        return
      }
      if (direction === "down" && index === this.currentForm.sections.length - 1) {
        return
      }

      if (direction === "up") {
        // eslint-disable-next-line vue/no-mutating-props
        this.currentForm.sections = [
          ...this.currentForm.sections.slice(0, index - 1),
          this.currentForm.sections[index],
          this.currentForm.sections[index - 1],
          ...this.currentForm.sections.slice(index + 1)
        ]
      } else {
        // eslint-disable-next-line vue/no-mutating-props
        this.currentForm.sections = [
          ...this.currentForm.sections.slice(0, index),
          this.currentForm.sections[index + 1],
          this.currentForm.sections[index],
          ...this.currentForm.sections.slice(index + 2)
        ]
      }
      const newIndex = direction === "up" ? index - 1 : index + 1
      this.sectionIndex = newIndex
    }
  }
}
</script>
